import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, skipWhile, switchMap } from 'rxjs/operators';
import { RequestService } from 'src/app/core/service/request.service';

@Injectable({
  providedIn: 'root',
})
export class BcpService {
  constructor(private restApi: RequestService) {}

  loadActiveBcp(options = {}) {
    return this.loadBcp({
      ...options,
      'isEnabled.equals': true,
    });
  }

  loadBcp(options = {}) {
    return this.restApi.request('GET', '/api/v5/bcps/bcc', options);
  }

  createBcp(data) {
    return this.restApi.request('POST', '/api/v5/bcps/create', data);
  }
  createBcpWithWorkflow(data) {
    return this.restApi.request('POST', '/api/v5/bcps/work-flow/create', data);
  }
  updateBcpWithWorkflow(data: any) {
    return this.restApi.request('PUT', '/api/v5/bcp/update-workflow', data);
  }

  getBcpByOrgId(options = {}) {
    return this.restApi.request('GET', '/api/bcps', {
      ...options,
      'isEnabled.equals': true,
      page: 0,
      size: 10000,
    });
  }

  getBcpDetail(bcpId) {
    return this.restApi.get(
      `/api/v5/work-flow-activities/module-ref/${bcpId}/module-name/BCP`
    );
  }

  updateBcpStatusUnderBia(biaId, isActive = false) {
    return this.loadBcp({
      'biaId.equals': biaId,
      'isActive.equals': !isActive,
      'isEnabled.equals': true,
    }).pipe(
      switchMap((resp) => {
        const bcps = resp.body;
        const services = bcps.reduce(
          (all, bcp) => {
            all['bcp' + bcp.id] = this.updateBcp(bcp.id, { ...bcp, isActive });
            return all;
          },
          { default: of(null) }
        );

        return forkJoin(services);
      })
    );
  }

  updateBcp(bcpId, data) {
    return this.restApi.request('PUT', `/api/v5/bcps/${bcpId}`, data);
  }

  updateBcpAlongwithWorkFlow(bcpId, data) {
    return this.restApi.request('PUT', `/api/v5/bcps/${bcpId}/workflow`, data);
  }

  getRecoveryPhaseTypes() {
    return this.restApi
      .get('/api/v5/bcp-recovery-phase-types')
      .pipe(map((response) => response.body));
  }

  loadRecoveryPhaseReporter() {
    return this.restApi
      .get('/api/v5/bcp-recovery-phase-reporters', {
        'isAddedbySystemAdmin.equals': true,
        'isActive.equals': true,
        'isEnabled.equals': true,
      })
      .pipe(map((resp) => resp.body));
  }

  loadRecoveryPhaseMapping(bcpId) {
    return this.restApi
      .get('/api/v5/bcp-recovery-reporter-mappings', {
        'bcpId.equals': bcpId,
        'isEnabled.equals': true,
        'isActive.equals': true,
        page: 0,
        size: 10000,
      })
      .pipe(
        map((resp) => resp.body),
        map((mappings) =>
          mappings
            .sort((m1, m2) => m1.bcp.id - m2.bcp.id || m1.order - m2.order)
            .map(({ reporters, id, order }) => ({
              ...reporters,
              mappingId: id,
              mOrder: order,
            }))
        )
      );
  }

  loadBcpRecoveryPhaseAll(bcpId) {
    const services = {
      mappings: this.loadRecoveryPhaseMapping(bcpId),
      reporters: this.loadRecoveryPhaseReporter(),
    };

    return forkJoin(services).pipe(
      skipWhile(({ reporters }: any) => !reporters.length),
      switchMap(({ mappings, reporters }) => {
        if (mappings.length === 0) {
          const addMappingServices = reporters.reduce((all, reporter, idx) => {
            all[`ads-${idx}`] = this.addBcpRecoveryPhaseMapping({
              bcp: { id: bcpId },
              reporters: { id: reporter.id },
              isEnabled: true,
              order: 0,
            });
            return all;
          }, {});
          return forkJoin(addMappingServices).pipe(
            switchMap((_) => this.loadRecoveryPhaseMapping(bcpId))
          );
        }
        return of(mappings);
      })
    );
  }

  addBcpRecoveryPhase(data) {
    return this.restApi.request(
      'POST',
      '/api/v5/bcp-recovery-phase-reporters-with-mapping',
      data
    );
  }

  addBcpRecoveryPhaseMapping(data) {
    return this.restApi.request(
      'POST',
      '/api/v5/bcp-recovery-reporter-mappings',
      data
    );
  }

  updateRecoveryPhase(data) {
    return this.restApi.request(
      'PUT',
      `/api/v5/bcp-recovery-phase-reporters/${data.id}`,
      data
    );
  }

  deleteRecoveryPhase(rpId) {
    return this.restApi.request(
      'DELETE',
      `/api/v5/bcp-recovery-phase-reporters/${rpId}`
    );
  }

  reorderRecoveryPhases(data) {
    return this.restApi.request(
      'PUT',
      '/api/v5/bcp-recovery-reporter-mappings/update-order',
      data
    );
  }

  createScenario(data) {
    return this.restApi.request('POST', '/api/v5/bcp-scenarios', data);
  }

  getBcpScenario(bcpId) {
    return this.restApi.request('GET', '/api/v5/bcp-scenarios-custom', {
      'bcpId.equals': bcpId,
    });
  }

  getBcpScenarioNew(bcpId) {
    return this.restApi.request('GET', '/api/v5/bcp-scenarios-news', {
      'bcpId.equals': bcpId,
    });
  }
  getBcpStrategyInfoByBcpId(bcpId) {
    return this.restApi.request('GET', '/api/v5/bcp-strategy-informations', {
      'bcpId.equals': bcpId,
    });
  }

  createScenarioNew(data) {
    return this.restApi.request('POST', '/api/bcp-scenarios-news', data);
  }

  updateScenarioNew(data) {
    return this.restApi.request(
      'PUT',
      `/api/bcp-scenarios-news/${data.id}`,
      data
    );
  }

  getBcpBiaProcesses(bcpId) {
    return this.restApi.request('GET', `/api/v5/bcp/${bcpId}/processes`);
  }

  updateBcpScenario(id, data) {
    return this.restApi.request('PUT', `/api/v5/bcp-scenarios/${id}`, data);
  }

  getStrategy() {
    return this.restApi.get(
      `/api/v5/sa-bcp-strategies?isEnabled.equals=true&isActive.equals=true&size=10000`
    );
  }

  getBcpStrategyStates() {
    return this.restApi.get('/api/v5/bcp-strategy-statuses');
  }

  addBcpStrategy(data) {
    return this.restApi.request('POST', '/api/v5/bcp-strategies', data);
  }

  getBcpStrategy(bcpId) {
    return this.restApi.request('GET', '/api/v5/bcp-strategies', {
      'bcpId.equals': bcpId,
      'isEnabled.equals': true,
    });
  }

  getBcpStrategyInformationById(id) {
    return this.restApi.get(`/api/v5/bcp-strategy-informations/${id}`);
  }

  getBcpStrategyInformation(bcpId, options = {}) {
    const data = JSON.parse(
      JSON.stringify({
        'bcpId.equals': bcpId,
        'isEnabled.equals': true,
        ...options,
      })
    );

    return this.restApi
      .get('/api/v5/bcp-strategy-informations', data)
      .pipe(map((resp) => resp.body));
  }

  createBcpStrategyInformation(data) {
    return this.restApi.request(
      'POST',
      '/api/v5/bcp-strategy-informations',
      data
    );
  }

  updateBcpStrategyInformation(data) {
    return this.restApi.request(
      'PUT',
      `/api/v5/bcp-strategy-informations/${data.id}`,
      data
    );
  }

  // BCP Internal External Staff
  getIntExtStaff(bcpId) {
    const services = {
      saStaffResp: this.restApi.get('/api/v5/bcp-internal-external-staffs', {
        'isAddedbySystemAdmin.equals': true,
        'isEnabled.equals': true,
        'isActive.equals': true,
        page: 0,
        size: 10000,
      }),
      bcpStaffResp: this.restApi.get('/api/v5/bcp-internal-external-staffs', {
        'bcpId.equals': bcpId,
        'isEnabled.equals': true,
        'isActive.equals': true,
        page: 0,
        size: 10000,
      }),
    };

    return forkJoin(services).pipe(
      map(({ saStaffResp, bcpStaffResp }: any) => ({
        staffs: [...saStaffResp.body, ...bcpStaffResp.body],
        count:
          +saStaffResp.headers.get('x-total-count') +
          +bcpStaffResp.headers.get('x-total-count'),
      }))
    );
  }

  addIntExtStaff(data) {
    return this.restApi.request(
      'POST',
      '/api/v5/bcp-internal-external-staffs',
      data
    );
  }

  updateIntExtStaff(data) {
    return this.restApi.request(
      'PUT',
      `/api/v5/bcp-internal-external-staffs/${data.id}`,
      data
    );
  }

  // Additional Information
  getAdditionalInformation(bcpId) {
    return this.restApi.get('/api/v5/bcp-additonal-infos', {
      'bcpId.equals': bcpId,
    });
  }

  private prepareAdditionalInformation({ file, fileDescription, ...jsonData }) {
    const formData = new FormData();
    formData.append('bcpAdditionalInfoDto', JSON.stringify(jsonData));
    formData.append('fileDescription', fileDescription);
    if (file) {
      formData.append('file', file);
    }
    return formData;
  }

  addAdditionalInformation(data) {
    const formData = this.prepareAdditionalInformation(data);
    return this.restApi.request(
      'POST',
      '/api/v5/bcp-additonal-infos/create',
      formData
    );
  }

  private prepareAdditionalInformations({
    file,
    fileDescription,
    ...jsonData
  }) {
    const formData = new FormData();
    formData.append('bcpAdditionalInfoDto', JSON.stringify(jsonData));
    formData.append('fileDescription', fileDescription);
    if (file) {
      formData.append('file', file);
    }
    return formData;
  }

  updateAdditionalInformation(adInfoId, data) {
    const formData = this.prepareAdditionalInformations(data);
    return this.restApi.request(
      'PUT',
      `/api/v5/bcp-additonal-infos/${adInfoId}/update`,
      formData
    );
  }

  downloadFile(fileName) {
    return this.restApi.get(`/api/v5/downloadFile/${fileName}`, undefined, {
      responseType: 'blob',
    });
  }

  loadBiaStaffRequirements(biaIds, locationId) {
    const services = {};

    const apiUrl = '/api/v5/bia-staff-requirements';

    if (locationId) {
      services['with_location'] = this.restApi.get(apiUrl, {
        'biaId.in': biaIds,
        'locationId.equals': locationId,
      });
      services['no_location'] = this.restApi.get(apiUrl, {
        'biaId.in': biaIds,
        'locationId.specified': false,
      });
    } else {
      services['no_location'] = this.restApi.get(apiUrl, {
        'biaId.in': biaIds,
      });
    }

    return forkJoin(services).pipe(
      map((response: any) =>
        Object.values(response).reduce(
          (all: any[], { body }: any) => all.concat(body),
          []
        )
      )
    );
  }

  loadContacts(filters) {
    return this.restApi.get('/api/v5/bcp-contacts', filters);
  }

  addBcpContacts(contacts) {
    const services = contacts.reduce((all, cur, index) => {
      all['contact-' + index] = this.restApi.request(
        'POST',
        '/api/v5/bcp-contacts',
        cur
      );
      return all;
    }, {});

    return forkJoin(services);
  }

  updateContacts(contacts) {
    const services = contacts.reduce((all, contact, index) => {
      all['contact-' + index] = this.restApi.request(
        'PUT',
        `/api/v5/bcp-contacts/${contact.id}`,
        contact
      );
      return all;
    }, {});

    return forkJoin(services);
  }

  getSoftwareDetails(options = {}) {
    return this.restApi.request('GET', '/api/v5/bcp-software-details', options);
  }

  getHardwateDetails(options = {}) {
    return this.restApi.request('GET', '/api/v5/bcp-hardware-details', options);
  }

  addHardwareDetails(data) {
    return this.restApi.request('POST', '/api/v5/bcp-hardware-details', data);
  }

  updateHardwareDetails(id, data) {
    return this.restApi.request(
      'PUT',
      `/api/v5/bcp-hardware-details/${id}`,
      data
    );
  }

  loadBcpRecoveryStaff(options) {
    return this.restApi.get('/api/v5/bcp-recovery-staffs', options);
  }

  addBcpRecoveryStaff(recoveryStaff) {
    return this.restApi.request(
      'POST',
      '/api/v5/bcp-recovery-staffs',
      recoveryStaff
    );
  }

  addBcpRecoveryStaffs(recoveryStaffs) {
    const services = recoveryStaffs.reduce((all, recoveryStaff, idx) => {
      all['rs-' + idx] = this.addBcpRecoveryStaff(recoveryStaff);
      return all;
    }, {});

    return forkJoin(services);
  }

  updateBcpRecoveryStaff(recoveryStaff) {
    return this.restApi.request(
      'PUT',
      `/api/v5/bcp-recovery-staffs/${recoveryStaff.id}`,
      recoveryStaff
    );
  }

  updateBcpRecoveryStaffs(recoveryStaffs) {
    const services = recoveryStaffs.reduce((all, recoveryStaff, idx) => {
      all['rs-' + idx] = this.updateBcpRecoveryStaff(recoveryStaff);
      return all;
    }, {});

    return forkJoin(services);
  }

  deleteBcpRecoveryStaff(recoveryStaff) {
    return this.restApi
      .request('DELETE', `/api/v5/bcp-recovery-staffs/${recoveryStaff.id}`)
      .pipe(catchError(() => of(null)));
  }

  deleteBcpRecoveryStaffs(recoveryStaffs) {
    const services = recoveryStaffs.reduce((all, recoveryStaff, idx) => {
      all['rs-' + idx] = this.deleteBcpRecoveryStaff(recoveryStaff);
      return all;
    }, {});

    return forkJoin(services);
  }

  loadBcpStaffGroup(options) {
    return this.restApi.get('/api/v5/bcp-staff-groups', options);
  }

  addBcpStaffGroup(staffGroup) {
    return this.restApi.request('POST', '/api/v5/bcp-staff-groups', staffGroup);
  }

  addBcpStaffGroups(staffGroups) {
    const services = staffGroups.reduce((all, staffGroup, idx) => {
      if (staffGroup.id) {
        all['sg-' + idx] = this.updateBcpStaffGroup(staffGroup);
      } else {
        all['sg-' + idx] = this.addBcpStaffGroup(staffGroup);
      }
      return all;
    }, {});

    return forkJoin(services);
  }

  updateBcpStaffGroup(staffGroup) {
    return this.restApi.request(
      'PUT',
      `/api/v5/bcp-staff-groups/${staffGroup.id}`,
      staffGroup
    );
  }

  updateBcpStaffGroups(staffGroups) {
    const services = staffGroups.reduce((all, staffGroup, idx) => {
      if (staffGroup.id) {
        all['sg-' + idx] = this.updateBcpStaffGroup(staffGroup);
      } else {
        all['sg-' + idx] = this.addBcpStaffGroup(staffGroup);
      }
      return all;
    }, {});

    return forkJoin(services);
  }

  private prepareBcpIntros({ file, ...jsonData }) {
    const formData = new FormData();
    formData.append('bcpIntroDto', JSON.stringify(jsonData));
    if (file) {
      formData.append('file', file);
    }
    return formData;
  }

  addBcpIntro(data) {
    const formData = this.prepareBcpIntros(data);
    return this.restApi.request('POST', '/api/v5/bcp-intros/create', formData);
  }

  updateBcpIntro = (id, data) => {
    return this.restApi.request('PUT', `/api/v5/bcp-intros/${id}/update`, data);
  };

  deleteBcpIntroFile(id: any, fileId: number): Observable<any> {
    return this.restApi.request(
      'DELETE',
      `/api/v5/bcp-intros/${id}/file/${fileId}`
    );
  }

  getBcpIntro = (bcpId) =>
    this.restApi.get('/api/v5/bcp-intros', {
      'bcpId.equals': bcpId,
    });

  addSaBcpIntro = (data) => {
    return this.restApi.request('POST', '/api/v5/sa-bcp-intros', data);
  };

  updateSaBcpIntro = (id, data) => {
    return this.restApi.request(
      'PUT',
      `/api/v5/sa-bcp-intros/${id}/update`,
      data
    );
  };

  deleteSaBcpIntroFile(id: any, fileId: number): Observable<any> {
    return this.restApi.request(
      'DELETE',
      `/api/v5/sa-bcp-intros/${id}/file/${fileId}`
    );
  }
  getSaBcpIntro() {
    return this.restApi.get('/api/v5/sa-bcp-intros', {});
  }

  openFileAfterDownloading = (response, file) => {
    const blob = new Blob([response.body], { type: response.body.type });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = file.fileName;
    link.click();
  };

  updateFrequency(id, data) {
    return this.restApi.request(
      'PUT',
      `/api/sa-bcp-inv-report-frequencies/${id}`,
      data
    );
  }
}
